import { useEffect, useRef } from 'react';
import { shortcutLabel, type HotkeyAction } from './hotkeys';

const TOOLTIP_DELAY_MS = 500, TOOLTIP_GAP = 6;
const MODIFIERS = new Set(['Shift', 'Control', 'Alt', 'Meta']);

/**
 * The editor's tooltips, one for the whole page: any element with `data-tip` shows it after 500 ms of hover or on keyboard
 * focus, never under a finger. `data-tip-key` adds the key of an editor command from `HOTKEYS`; `data-tip-side="above"`
 * puts it over the element instead of under it. A press, a key, a scroll or the pointer leaving hides it. It is a popover shown again each time, so it stands over open menus.
 */
export function TooltipLayer() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const tip = ref.current;
    if (!tip) return;
    const [name, keys] = tip.children as unknown as [HTMLElement, HTMLElement];
    let timer = 0, frame = 0, target: HTMLElement | null = null, pressed: HTMLElement | null = null;
    const hide = () => {
      window.clearTimeout(timer); cancelAnimationFrame(frame); target = null;
      if (tip.matches(':popover-open')) tip.hidePopover();
    };
    // A target that leaves the page (C opened the catalog over its pill, a menu closed) takes its tooltip with it.
    const watch = () => { if (!target?.isConnected) hide(); else frame = requestAnimationFrame(watch); };
    const show = () => {
      const element = target;
      if (!element?.isConnected || !element.dataset.tip || element.getAttribute('aria-expanded') === 'true') { hide(); return; }
      name.textContent = element.dataset.tip;
      const key = element.dataset.tipKey as HotkeyAction | undefined;
      keys.textContent = key ? shortcutLabel(key) : ''; keys.hidden = !key;
      if (tip.matches(':popover-open')) tip.hidePopover();
      tip.style.left = '0px'; tip.style.top = '0px';
      tip.showPopover();
      const box = element.getBoundingClientRect(), width = tip.offsetWidth, height = tip.offsetHeight;
      const above = element.dataset.tipSide === 'above';
      tip.style.left = `${Math.max(8, Math.min(box.left + box.width / 2 - width / 2, innerWidth - 8 - width))}px`;
      tip.style.top = `${above ? box.top - TOOLTIP_GAP - height : box.bottom + TOOLTIP_GAP}px`;
      frame = requestAnimationFrame(watch);
    };
    const arm = (element: HTMLElement | null) => {
      if (element === target) return;
      hide();
      if (!element || element.getAttribute('aria-expanded') === 'true') return;
      target = element; timer = window.setTimeout(show, TOOLTIP_DELAY_MS);
    };
    const tipOf = (node: EventTarget | null) => (node instanceof Element ? node.closest<HTMLElement>('[data-tip]') : null);
    const over = (event: PointerEvent) => {
      if (event.pointerType === 'touch' || event.buttons) { hide(); return; }
      const element = tipOf(event.target);
      // The button just pressed stays quiet until the pointer has left it.
      if (element && element === pressed) return;
      pressed = null; arm(element);
    };
    const press = (event: PointerEvent) => { hide(); pressed = tipOf(event.target); };
    const out = (event: PointerEvent) => { if (!event.relatedTarget) hide(); };
    const focus = (event: FocusEvent) => { const element = tipOf(event.target); if (element?.matches(':focus-visible')) arm(element); };
    const blur = (event: FocusEvent) => { if (tipOf(event.target) === target) hide(); };
    const key = (event: KeyboardEvent) => { if (!MODIFIERS.has(event.key)) hide(); };
    document.addEventListener('pointerover', over); document.addEventListener('pointerout', out);
    document.addEventListener('focusin', focus); document.addEventListener('focusout', blur);
    document.addEventListener('pointerdown', press, true); document.addEventListener('keydown', key, true);
    window.addEventListener('scroll', hide, true); window.addEventListener('blur', hide);
    return () => {
      hide();
      document.removeEventListener('pointerover', over); document.removeEventListener('pointerout', out);
      document.removeEventListener('focusin', focus); document.removeEventListener('focusout', blur);
      document.removeEventListener('pointerdown', press, true); document.removeEventListener('keydown', key, true);
      window.removeEventListener('scroll', hide, true); window.removeEventListener('blur', hide);
    };
  }, []);
  return <div ref={ref} className="tooltip" popover="manual" role="tooltip" aria-hidden="true"><span className="tooltip-name" /><span className="tooltip-key" /></div>;
}
